/**
 * NotificationContext - Manages in-app notifications for drivers/charters
 * 
 * Features:
 * - Polls for new notifications
 * - Shows toast when new notification arrives
 * - Provides notification bell component
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useLanguage } from './LanguageContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const lastNotificationIdRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Mark single notification as read
  const markAsRead = useCallback(async (notificationId) => {
    if (!token) return;
    
    try {
      await axios.post(`${API_URL}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, [token]);

  // Show toast for new notification
  const showNotificationToast = useCallback((notification) => {
    // Translate i18n keys to actual text
    const title = notification.title.startsWith('notifications.') 
      ? t(notification.title) 
      : notification.title;
    const message = notification.message.startsWith('notifications.') 
      ? t(notification.message) 
      : notification.message;
    
    toast(title, {
      description: message,
      duration: 8000,
      action: {
        label: t('notifications.newTrip.cta') || 'Bekijk',
        onClick: () => {
          markAsRead(notification.id);
          navigate(notification.action_url || '/driver/trips');
        }
      }
    });
  }, [t, navigate, markAsRead]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newNotifications = response.data || [];
      setNotifications(newNotifications);
      
      // Check for new unread notifications and show toast
      const unreadNotifications = newNotifications.filter(n => !n.is_read);
      setUnreadCount(unreadNotifications.length);
      
      // Show toast for new notifications (only if we have a previous reference)
      if (lastNotificationIdRef.current && unreadNotifications.length > 0) {
        const latestNotification = unreadNotifications[0];
        if (latestNotification.id !== lastNotificationIdRef.current) {
          // New notification arrived! Show toast
          showNotificationToast(latestNotification);
        }
      }
      
      // Update reference to latest notification
      if (unreadNotifications.length > 0) {
        lastNotificationIdRef.current = unreadNotifications[0].id;
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [token, showNotificationToast]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!token) return;
    
    try {
      await axios.post(`${API_URL}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [token]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    if (!token) return;
    
    try {
      await axios.delete(`${API_URL}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }, [token]);

  // Start polling for notifications (only for drivers/charters)
  useEffect(() => {
    if (!token || !user) return;
    
    // Only poll for drivers and charters
    const role = user?.role || user?.user_role || '';
    if (!['driver', 'charter'].includes(role)) return;
    
    // Initial fetch with timeout to avoid direct setState
    const initialFetchTimeout = setTimeout(() => {
      fetchNotifications();
    }, 100);
    
    // Poll every 30 seconds (reduced from 10s for performance)
    pollingIntervalRef.current = setInterval(fetchNotifications, 30000);
    
    return () => {
      clearTimeout(initialFetchTimeout);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [token, user, fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    // Return mock values if used outside provider (for non-driver pages)
    return {
      notifications: [],
      unreadCount: 0,
      loading: false,
      fetchNotifications: () => {},
      markAsRead: () => {},
      markAllAsRead: () => {},
      deleteNotification: () => {}
    };
  }
  return context;
};

export default NotificationContext;
